import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { EventCard, EventsGrid } from '@/components';
import { EngineService } from '@/services/engine';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useSourcesStore } from '@/stores';
import { usePipEventWatcher } from '@/stores/pip-player.store';
import { buildEventCardMetadataFromEvent } from '@/utils/event-card-metadata';
import type { SimilarResult } from '@/stores/query-results.store';

interface SimilarResultsProps {
    results: SimilarResult[];
    onEventClick: (id: string, event: MouseEvent<HTMLDivElement>) => void;
    onQuickView: (eventId: string) => void;
}

export function SimilarResults({
    results,
    onEventClick,
    onQuickView,
}: SimilarResultsProps) {
    const { t } = useTranslation(I18nNamespace.QUERIES);
    const getSourceById = useSourcesStore((state) => state.getSourceById);
    const isPipPlaying = usePipEventWatcher();
    if (results.length === 0) return null;

    return (
        <div className="pl-6 pr-4 pt-3 flex flex-col gap-2 animate-fadeIn">
            <p className="text-sm italic leading-relaxed text-grey-400">
                {t('similarResults.title')}
            </p>
            <EventsGrid>
                {results.map(({ scoredEvent, description }) => {
                    const event = scoredEvent.event;
                    return (
                        <EventCard
                            key={event.id}
                            id={event.id}
                            title={description}
                            thumbnailUrls={[
                                event.thumbnailUrl ||
                                    EngineService.getEventThumbnailUrl(
                                        event.id,
                                        true,
                                    ),
                            ]}
                            isScene={event.type === 'SCENE'}
                            metadata={buildEventCardMetadataFromEvent(
                                event,
                                getSourceById(event.sourceId),
                            )}
                            quickView={{
                                state: isPipPlaying(event.id)
                                    ? 'watching'
                                    : 'idle',
                                onClick: () => onQuickView(event.id),
                            }}
                            onClick={onEventClick}
                        />
                    );
                })}
            </EventsGrid>
        </div>
    );
}
